/**
 * 文件类型检测工具 - 支持魔法数字识别和特殊文件名处理
 * 
 * 支持的图片格式：jpg, jpeg, png, gif, bmp, heic, hevc, webp
 * 支持的视频格式：mp4, webm, ogg, mov, avi, mkv, flv, wmv, m4v, rmvb
 * 特殊文件支持：vsg_output_*, wxfile_*
 */

/**
 * 检测文件类型 - 支持魔法数字识别特殊文件名
 * @param {File} file 文件对象
 * @param {string} expectedType 期望的文件类型 ('image' 或 'video')
 * @returns {Promise<{isImage: boolean, isVideo: boolean, correctedFile: File, errorMsg?: string}>}
 * 
 * @example
 * const result = await detectFileType(file, 'image');
 * if (result.errorMsg) {
 *   console.error(result.errorMsg);
 * } else {
 *   console.log('检测到:', result.isImage ? '图片' : '视频');
 * }
 */
export function detectFileType(file, expectedType = 'image') {
  return new Promise((resolve) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif',  'bmp', 'heic', 'hevc','heif', 'webp'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v', 'rmvb'];
    
    // 获取文件扩展名
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isSpecialFile = file.name && (
      file.name.startsWith('vsg_output_') || 
      file.name.startsWith('wxfile_')
    );
    console.log(isSpecialFile,'<-- isSpecialFile',file)
    // 如果是特殊文件（vsg_output_ 或 wxfile_），需要读取魔法数字
    if (isSpecialFile || !file.type) {
      console.log('检测特殊文件:', file.name);
      
      // 读取文件头部来识别文件类型
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arr = new Uint8Array(e.target.result);
          
          // 图片文件的魔法数字
          const isJPEG = arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF;
          const isPNG = arr[0] === 0x89 && arr[1] === 0x50 && arr[2] === 0x4E && arr[3] === 0x47;
          const isGIF = arr[0] === 0x47 && arr[1] === 0x49 && arr[2] === 0x46;
          const isWebP = arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46 && arr[8] === 0x57 && arr[9] === 0x45 && arr[10] === 0x42 && arr[11] === 0x50;
          
          // 视频文件的魔法数字
          const isMP4 = arr[4] === 0x66 && arr[5] === 0x74 && arr[6] === 0x79 && arr[7] === 0x70;
          const isWebM = arr[0] === 0x1A && arr[1] === 0x45 && arr[2] === 0xDF && arr[3] === 0xA3;
          const isMOV = arr[4] === 0x6D && arr[5] === 0x6F && arr[6] === 0x6F && arr[7] === 0x76;
          const isAVI = arr[0] === 0x52 && arr[1] === 0x49 && arr[2] === 0x46 && arr[3] === 0x46;
          
          let detectedType = null;
          let extension = null;
          let mimeType = null;
          
          // 检测到图片
          if (isJPEG) {
            detectedType = 'image';
            extension = '.jpg';
            mimeType = 'image/jpeg';
          } else if (isPNG) {
            detectedType = 'image';
            extension = '.png';
            mimeType = 'image/png';
          } else if (isGIF) {
            detectedType = 'image';
            extension = '.gif';
            mimeType = 'image/gif';
          } else if (isWebP) {
            detectedType = 'image';
            extension = '.webp';
            mimeType = 'image/webp';
          }
          // 检测到视频
          else if (isMP4) {
            detectedType = 'video';
            extension = '.mp4';
            mimeType = 'video/mp4';
          } else if (isWebM) {
            detectedType = 'video';
            extension = '.webm';
            mimeType = 'video/webm';
          } else if (isMOV) {
            detectedType = 'video';
            extension = '.mov';
            mimeType = 'video/quicktime';
          } else if (isAVI) {
            detectedType = 'video';
            extension = '.avi';
            mimeType = 'video/x-msvideo';
          }
          
          if (detectedType) {
            // vsg_output_ 文件类型检查
            if (file.name.startsWith('vsg_output_')) {
              if (expectedType === 'image' && detectedType !== 'image') {
                resolve({ 
                  isImage: false, 
                  isVideo: false, 
                  correctedFile: file,
                  errorMsg: '文件格式不正确'
                });
                return;
              }
              if (expectedType === 'video' && detectedType !== 'video') {
                resolve({ 
                  isImage: false, 
                  isVideo: false, 
                  correctedFile: file,
                  errorMsg: '文件格式不正确'
                });
                return;
              }
            }
            
            // 补全文件名后缀
            if (!file.name.toLowerCase().match(new RegExp(`\\${extension}$`))) {
              const baseName = file.name.includes('.') ? file.name.split('.')[0] : file.name;
              file._suggestedName = `${baseName}${extension}`;
            }
            
            // 记录文件类型检测
            file._detectedType = mimeType;
            
            resolve({ 
              isImage: detectedType === 'image', 
              isVideo: detectedType === 'video',
              correctedFile: file
            });
          } else {
            // 无法识别
            resolve({ 
              isImage: false, 
              isVideo: false, 
              correctedFile: file,
              errorMsg: '文件格式不正确'
            });
          }
        } catch (error) {
          console.warn('detectFileType: 解析文件头部失败', error);
          resolve({ 
            isImage: false, 
            isVideo: false, 
            correctedFile: file,
            errorMsg: '文件格式不正确'
          });
        }
      };
      
      reader.onerror = () => {
        console.warn('detectFileType: 读取文件失败');
        resolve({ 
          isImage: false, 
          isVideo: false, 
          correctedFile: file,
          errorMsg: '文件格式不正确'
        });
      };
      
      try {
        // WebP 头部识别需要读取到 12 字节（RIFF....WEBP）
        reader.readAsArrayBuffer(file.slice(0, 12));
      } catch (error) {
        console.warn('detectFileType: 文件切片失败', error);
        resolve({ 
          isImage: false, 
          isVideo: false, 
          correctedFile: file,
          errorMsg: '文件格式不正确'
        });
      }
    } else {
      // 常规文件，通过扩展名判断
      // 如果有扩展名但不在允许列表中，直接失败
      if (fileExtension) {
        const isValidImage = imageExtensions.includes(fileExtension);
        const isValidVideo = videoExtensions.includes(fileExtension);
        
        if (!isValidImage && !isValidVideo) {
          console.log('文件扩展名不被支持:', fileExtension);
          resolve({ 
            isImage: false, 
            isVideo: false, 
            correctedFile: file,
            errorMsg: '文件格式不正确'
          });
          return;
        }
        
        // MIME 类型检查
        if (file.type.startsWith('image/')) {
          if (!isValidImage) {
            resolve({ 
              isImage: false, 
              isVideo: false, 
              correctedFile: file,
              errorMsg: '文件格式不正确'
            });
            return;
          }
          // 补全扩展名（如果需要）
          if (!file.name.match(/\.(jpg|jpeg|png|gif|webp|webm|bmp|heic|hevc|heif)$/i)) {
            let extension = '.jpg';
            if (file.type === 'image/png') extension = '.png';
            if (file.type === 'image/gif') extension = '.gif';
            if (file.type === 'image/webm') extension = '.webm';
            if (file.type === 'image/webp') extension = '.webp';
            file._suggestedName = `${file.name}${extension}`;
          }
          resolve({ isImage: true, isVideo: false, correctedFile: file });
        } else if (file.type.startsWith('video/')) {
          if (!isValidVideo) {
            resolve({ 
              isImage: false, 
              isVideo: false, 
              correctedFile: file,
              errorMsg: '文件格式不正确'
            });
            return;
          }
          // 补全扩展名（如果需要）
          if (!file.name.match(/\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv|m4v|rmvb)$/i)) {
            let extension = '.mp4';
            if (file.type === 'video/webm') extension = '.webm';
            if (file.type === 'video/quicktime') extension = '.mov';
            if (file.type === 'video/x-msvideo') extension = '.avi';
            file._suggestedName = `${file.name}${extension}`;
          }
          resolve({ isVideo: true, isImage: false, correctedFile: file });
        } else {
          // 未知的 MIME 类型，通过扩展名判断
          if (isValidImage) {
            resolve({ isImage: true, isVideo: false, correctedFile: file });
          } else if (isValidVideo) {
            resolve({ isVideo: true, isImage: false, correctedFile: file });
          } else {
            resolve({ 
              isImage: false, 
              isVideo: false, 
              correctedFile: file,
              errorMsg: '文件格式不正确'
            });
          }
        }
      } else {
        // 没有扩展名
        if (file.type.startsWith('image/')) {
          resolve({ isImage: true, isVideo: false, correctedFile: file });
        } else if (file.type.startsWith('video/')) {
          resolve({ isVideo: true, isImage: false, correctedFile: file });
        } else {
          resolve({ 
            isImage: false, 
            isVideo: false, 
            correctedFile: file,
            errorMsg: '文件格式不正确'
          });
        }
      }
    }
  });
}

export default detectFileType;
